import { useEffect, useRef, type ReactNode } from 'react';
import { Icon } from './Icon';
import estilos from './Modal.module.css';

interface ModalProps {
  abierto: boolean;
  onCerrar: () => void;
  titulo: string;
  descripcion?: string;
  children: ReactNode;
  /** Botones de acción. Se colocan al pie, alineados a la derecha. */
  pie?: ReactNode;
  ancho?: boolean;
  /**
   * Impide cerrar con Escape o pulsando fuera.
   * Reservado para procesos en curso que no deben interrumpirse a medias.
   */
  bloqueado?: boolean;
}

/**
 * Diálogo modal.
 *
 * Se apoya en el elemento `<dialog>` del navegador, que ya resuelve el
 * atrapado del foco, el cierre con Escape y el fondo inerte. En móvil se
 * presenta anclado abajo, al alcance del pulgar.
 */
export function Modal({
  abierto,
  onCerrar,
  titulo,
  descripcion,
  children,
  pie,
  ancho = false,
  bloqueado = false,
}: ModalProps) {
  const refDialogo = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialogo = refDialogo.current;
    if (!dialogo) return;

    if (abierto && !dialogo.open) dialogo.showModal();
    else if (!abierto && dialogo.open) dialogo.close();
  }, [abierto]);

  useEffect(() => {
    const dialogo = refDialogo.current;
    if (!dialogo) return;

    // Escape dispara `cancel`; lo interceptamos para que el cierre pase
    // siempre por `onCerrar` y el estado de React no se desincronice.
    const alCancelar = (e: Event) => {
      e.preventDefault();
      if (!bloqueado) onCerrar();
    };

    dialogo.addEventListener('cancel', alCancelar);
    return () => dialogo.removeEventListener('cancel', alCancelar);
  }, [onCerrar, bloqueado]);

  /** Pulsar el fondo cierra; pulsar el contenido no. */
  const alPulsarFondo = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (bloqueado || e.target !== refDialogo.current) return;
    onCerrar();
  };

  return (
    <dialog
      ref={refDialogo}
      className={[estilos.dialogo, ancho ? estilos.ancho : ''].filter(Boolean).join(' ')}
      onClick={alPulsarFondo}
      aria-labelledby="modal-titulo"
    >
      <div className={estilos.contenedor}>
        <header className={estilos.encabezado}>
          <div>
            <h2 className={estilos.titulo} id="modal-titulo">
              {titulo}
            </h2>
            {descripcion && <p className={estilos.descripcion}>{descripcion}</p>}
          </div>
          {!bloqueado && (
            <button type="button" className={estilos.cerrar} onClick={onCerrar} aria-label="Cerrar">
              <Icon nombre="cerrar" tamano={20} />
            </button>
          )}
        </header>

        <div className={estilos.cuerpo}>{children}</div>

        {pie && <footer className={estilos.pie}>{pie}</footer>}
      </div>
    </dialog>
  );
}
